import { resolveColor } from './settingValue.js';
const GROUP = 'Section';
// Shopify parity (founder 2026-07-04): the section SIZE control — Top/Bottom padding — lives in its own
// clearly-named "Section padding" group (not buried with colour scheme + width), so making a section
// bigger/smaller is as discoverable as it is in the Shopify theme editor. The universalFields spread last
// in each schema, so this group renders near the BOTTOM of the inspector (Shopify's placement). Desktop +
// mobile stay two groups so the short "Top/Bottom padding" labels never need a cluttering "(mobile)" tag.
// 'Animations', not 'Animation': the timeline section already groups its own motion controls under that
// exact heading, and it is already translated in the seven dictionaries. Two near-identical group names in
// one inspector would be a defect of our own making.
const ANIMATION_GROUP = 'Animations';
const PADDING_GROUP = 'Section padding';
const PADDING_GROUP_MOBILE = 'Section padding (mobile)';
/**
 * The three Animation fields on their own, for the sections that hand-roll their universal group instead
 * of spreading {@link universalFields} — custom-columns declares its own colour scheme and padding, so it
 * would otherwise be the one section in the inspector with no Animation group at all (founder, 2026-09-18:
 * "multicolumn here doesn't have an animation").
 *
 * Adopting them is TWO lines, not one: the fields here, and the values passed through to
 * {@link universalSectionProps} — a hand-rolled section builds its settings object by hand, so a new
 * universal setting does not flow into it on its own.
 */
export const animationFields = [
    // Default 'none' ON PURPOSE: this group arrives on sections that are already live in real shops, and a
    // seller who never asked for movement must not find her storefront animated one morning. New theme
    // presets ship 'rise' instead, so a shop created after this lands is animated out of the box.
    {
        id: 'animation',
        type: 'select',
        label: 'Animation',
        group: ANIMATION_GROUP,
        default: 'none',
        options: [
            { value: 'none', label: 'None' },
            // The choreographed entrance (2026-09-20): the picture is unmasked first, then every LINE of text
            // rises in behind its own mask, one after the other, then the buttons — in every section, the
            // engine reading the section's real DOM by role. See revealChoreo.ts.
            { value: 'reveal', label: 'Unveil' },
            { value: 'fade', label: 'Fade in' },
            { value: 'rise', label: 'Rise up' },
            { value: 'slide-left', label: 'Slide from left' },
            { value: 'slide-right', label: 'Slide from right' },
            { value: 'zoom', label: 'Zoom in' },
            { value: 'blur', label: 'Sharpen' },
        ],
        info: 'Plays once, the first time the section reaches the shopper. Unveil shows the picture first, then every line of text one after another. Never plays for a shopper who has asked their device to reduce motion.',
    },
    // `animationPace`, not the obvious `animationSpeed`: collapsible-content already ships a field by that
    // id for its accordion's open/close timing, and `field.animationSpeed` is already translated into the
    // seven dictionaries as "Vitesse d'ouverture et de fermeture". A universal field reusing the id would
    // have inherited that sentence in every language — and collided outright the day that section adopts
    // the universal group.
    {
        id: 'animationPace',
        type: 'select',
        label: 'Speed',
        group: ANIMATION_GROUP,
        default: 'normal',
        options: [
            { value: 'slow', label: 'Slow' },
            { value: 'normal', label: 'Normal' },
            { value: 'fast', label: 'Fast' },
        ],
        visibleWhen: { field: 'animation', notEquals: 'none' },
    },
    {
        id: 'animationStagger',
        type: 'checkbox',
        label: 'One item at a time',
        group: ANIMATION_GROUP,
        default: false,
        // Hidden for Unveil, which ALWAYS sequences the items (and everything inside them) — a box that
        // could not change anything is a dead control.
        visibleWhen: { field: 'animation', notEquals: ['none', 'reveal'] },
        info: 'The section stays put and its items arrive one after another. Sections that do not list items animate as one block either way.',
    },
];
/**
 * ONE STAGGERED ENTRANCE ITEM — spread onto the element a section wants to arrive on its own turn:
 *
 *     <li key={b.id} {...ANIM_ITEM}>…</li>
 *
 * Inert unless the seller ticks "One item at a time" on that section: the hiding rules in theme.css only
 * reach `[data-aqx-anim-item]` under an ancestor carrying `--aqx-anim-stagger`, and the engine only
 * numbers them (`--aqx-anim-i`) there. So a section marks its items ONCE and both settings keep working.
 *
 * WHAT COUNTS AS AN ITEM IS THE SECTION'S OWN ANSWER, and that is the whole point of the setting
 * (founder, 2026-09-18: the same box should not do the same thing on image-with-text as on multicolumn).
 * The taxonomy we ship:
 *   · a grid/rail of cards (multicolumn, image rail, collection grid, logos, testimonials…) → one CARD
 *   · a media + copy split (image-with-text, video-with-text)                                → the MEDIA
 *     column, then each content block after it, so the picture leads and the words follow it in
 *   · a stack of rows (multirow, FAQ, collapsible, timeline, comparison rows)                 → one ROW
 *   · a section heading that sits above any of those                                          → the heading,
 *     first, so the title introduces its own list instead of appearing with the first card
 * A section whose content is ONE thing (a single image, a hero, a ticker, a slideshow showing one slide at
 * a time) marks nothing and keeps the block entrance — see the `:has()` fallback in theme.css.
 */
export const ANIM_ITEM = { 'data-aqx-anim-item': '' };
/**
 * The section's own chrome — palette + container width. Split out of {@link universalFields} (18/09/2026)
 * so a section that lays its settings out PER DEVICE ("Desktop" / "Mobile" parts, image + video) can keep
 * this block where it belongs — under "Section", shared by both readers — while re-grouping its padding.
 * Every other section spreads `universalFields` and sees exactly what it saw before.
 */
export const sectionChromeFields = [
    {
        id: 'colorScheme',
        type: 'select',
        label: 'Color scheme',
        group: GROUP,
        default: 'light',
        options: [
            { value: 'light', label: 'Background 1' },
            { value: 'inverse', label: 'Inverse' },
            { value: 'accent', label: 'Accent' },
            { value: 'custom', label: 'Custom' },
        ],
    },
    // Custom band colours — only shown/read when Color scheme = Custom. Lets a section take a one-off brand
    // colour (e.g. a bold full-width band) WITHOUT repainting the whole store theme; mirrors the custom-colour
    // controls announcement-bar / footer already expose. Overrides the `--aqx-scheme-*` contract in-place
    // (universalSectionProps), so child blocks (price, buy-buttons, volume-tiers…) inherit the custom palette.
    {
        id: 'customBackground',
        type: 'color',
        label: 'Background',
        group: GROUP,
        default: { ref: 'custom', value: '#1E3A54' },
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    {
        id: 'customText',
        type: 'color',
        label: 'Text',
        group: GROUP,
        default: { ref: 'custom', value: '#ffffff' },
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    {
        id: 'width',
        type: 'select',
        label: 'Width',
        group: GROUP,
        default: 'standard',
        options: [
            { value: 'narrow', label: 'Narrow' },
            { value: 'standard', label: 'Standard' },
            { value: 'wide', label: 'Wide' },
            { value: 'full', label: 'Full width' },
        ],
    },
];
/**
 * The "size" control (Shopify's "Section padding"), under whatever heading the section files it.
 *
 * `group` is a parameter for ONE reason: a section laid out per device puts its desktop padding in the
 * "Desktop" part and its phone padding in the "Mobile" part, instead of two trailing padding groups the
 * seller has to scroll past both device parts to reach. The ids never move, so a stored document reads
 * the same whichever heading the inspector draws.
 */
export function paddingFields(group = PADDING_GROUP) {
    return [
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group,
            default: 48,
            min: 0,
            max: 160,
            step: 4,
            unit: 'px',
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group,
            default: 48,
            min: 0,
            max: 160,
            step: 4,
            unit: 'px',
        },
    ];
}
/**
 * Mobile padding — the phone's own top/bottom, rendered responsively via `universalSectionProps` + the
 * global ≤749px rule in theme.css (`--aqx-mpt` / `--aqx-mpb`).
 */
export function mobilePaddingFields(group = PADDING_GROUP_MOBILE) {
    return [
        {
            id: 'mobilePaddingTop',
            type: 'range',
            label: 'Top padding',
            group,
            default: 24,
            min: 0,
            max: 160,
            step: 4,
            unit: 'px',
        },
        {
            id: 'mobilePaddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group,
            default: 24,
            min: 0,
            max: 160,
            step: 4,
            unit: 'px',
        },
    ];
}
/** The universal section fields. Padding sits under its own "Section padding" group. Spread into `fields[]`. */
export const universalFields = [
    ...sectionChromeFields,
    ...paddingFields(),
    ...mobilePaddingFields(),
    ...animationFields,
];
/** Px behind each fixed {@link MediaRadiusValue} step (`theme` is not a px — it means "don't override"). */
export const MEDIA_RADIUS_PX = {
    none: 0,
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    '2xl': 32,
};
/**
 * "Image corners" — the ONE setting every media-bearing section declares (image-with-text, video, slideshow,
 * collage, product cards…). Default `theme` follows the global radius from the theme editor
 * (`shape.mediaRadius`, 0 = square out of the box — founder ask 2026-09-11); any other value pins this
 * section's images/videos to a fixed radius. Resolved by {@link mediaRadiusVars}, which
 * {@link universalSectionProps} already spreads — a section that builds its own root style spreads it itself.
 * Every image/video rule then reads `var(--aqx-theme-radius-media, 0px)`, never a hard-coded px.
 */
export const mediaRadiusField = {
    id: 'mediaRadius',
    type: 'select',
    label: 'Image corners',
    group: GROUP,
    default: 'theme',
    options: [
        { value: 'theme', label: 'Theme default' },
        { value: 'none', label: 'Square (0 px)' },
        { value: 'xs', label: '4 px' },
        { value: 'sm', label: '8 px' },
        { value: 'md', label: '12 px' },
        { value: 'lg', label: '16 px' },
        { value: 'xl', label: '24 px' },
        { value: '2xl', label: '32 px' },
    ],
    info: 'Corner radius of the images and videos in this section. "Theme default" follows Theme settings › Layout › Image corner radius.',
};
/**
 * The CSS var override for a section's chosen image radius: `{}` for `theme` (or anything unknown, so a
 * legacy/malformed value never breaks the theme), else `--aqx-theme-radius-media` re-declared on the root.
 */
export function mediaRadiusVars(s) {
    const v = s.mediaRadius;
    if (typeof v !== 'string' || v === 'theme')
        return {};
    const px = MEDIA_RADIUS_PX[v];
    if (typeof px !== 'number')
        return {};
    return { '--aqx-theme-radius-media': `${px}px` };
}
const WIDTH_MAX = {
    narrow: '720px',
    standard: '1024px',
    wide: 'var(--aqx-theme-container, 1200px)',
    full: '100%',
};
/** How far a `rise`/`slide` starts from, per animation. `none` = no transform, only the opacity/blur. */
const ANIMATION_FROM = {
    // Unveil moves nothing at the block level: its units carry the movement (revealChoreo.ts).
    reveal: 'none',
    fade: 'none',
    rise: 'translateY(var(--aqx-anim-distance, 48px))',
    'slide-left': 'translateX(calc(var(--aqx-anim-distance, 48px) * -1))',
    'slide-right': 'translateX(var(--aqx-anim-distance, 48px))',
    zoom: 'scale(0.94)',
    blur: 'none',
};
const ANIMATION_DURATION = {
    slow: '1s',
    normal: '0.6s',
    fast: '0.35s',
};
/** Unveil's own clock: each unit's movement, and the engine derives every gap from it (see schedule()). */
const REVEAL_DURATION = {
    slow: '1.3s',
    normal: '0.9s',
    fast: '0.6s',
};
/**
 * Narrowed to just the three animation keys rather than the whole `UniversalSettings`: several sections
 * define their own settings interface that only PARTLY matches it (collection-banner's `colorScheme` is
 * Shrine's five-value scale, not ours), and those are exactly the hand-rolled sections that need to call
 * this. Asking for the whole shape would have made the call site a cast.
 */
export function animationVars(s) {
    // Read defensively rather than requiring the caller's settings type to extend ours. Nineteen sections
    // declare their own settings interface that knows nothing about the universal group, and making each
    // one extend it would be nineteen edits for no added safety — the VALUES are what need checking, and
    // they come from a stored document, so they can be anything a past or future version wrote. An
    // animation name we do not recognise is treated as no animation, never emitted half-resolved.
    const raw = s;
    const anim = raw.animation;
    if (anim === undefined || anim === 'none' || !(anim in ANIMATION_FROM))
        return {};
    const pace = raw.animationPace;
    const table = anim === 'reveal' ? REVEAL_DURATION : ANIMATION_DURATION;
    const duration = table[pace !== undefined && pace in table ? pace : 'normal'];
    const vars = {
        '--aqx-anim-name': anim,
        '--aqx-anim-from': ANIMATION_FROM[anim],
        '--aqx-anim-duration': duration,
    };
    // A whole filter value, not a length: see the note in theme.css — `blur(0px)` is not `none`.
    if (anim === 'blur')
        vars['--aqx-anim-filter'] = 'blur(12px)';
    // The stagger flag is read by a CSS selector, so it must be a var the browser keeps in the attribute
    // TEXT — hence a value, not a bare presence. Never for Unveil: it sequences items on its own, and the
    // stagger rules would hide the items a second way.
    if (raw.animationStagger === true && anim !== 'reveal')
        vars['--aqx-anim-stagger'] = '1';
    return vars;
}
/**
 * A SECTION-SPECIFIC stagger step, in seconds, and only while the section is actually staggering.
 *
 * The global step is 0.08s, tuned on a rail of eight or twelve cards. On a section whose "items" are two
 * halves — a media + copy split — 80ms is not a sequence, it is a smear: the two arrive so nearly together
 * that the setting looks like it did nothing, which is the complaint this whole change answers. A section
 * that knows it has few, large items says so here and the value inherits down to its items.
 *
 * Returns `undefined` — not `{}` — when the animation is off or the seller did not ask for one item at a
 * time: React prints `style=""` for an empty object, and a shop with no movement must keep BYTE-identical
 * markup (the guard in animationCoverage.test.tsx).
 */
export function animStepVars(s, seconds) {
    const raw = s;
    if (raw.animationStagger !== true)
        return undefined;
    if (raw.animation === undefined || raw.animation === 'none' || !(raw.animation in ANIMATION_FROM))
        return undefined;
    if (raw.animation === 'reveal')
        return undefined;
    return { '--aqx-anim-step': seconds };
}
/**
 * The `--aqx-scheme-*` OVERRIDE for a section whose palette is `custom` — `{}` for every other scheme.
 *
 * Custom scheme: keep a valid base `data-scheme` (so every `--aqx-scheme-*` var still has a value) but
 * OVERRIDE the palette in-place from the seller's colours. Inline vars beat the `[data-scheme]` rule, and
 * every child block reads the same `--aqx-scheme-*` contract, so the whole band + its blocks re-theme.
 *
 * Exported because {@link universalSectionProps} is not the only caller: a section that builds its own root
 * style (collection-banner, which ships Shrine's 5-value scheme select instead of the universal one) has to
 * spread the SAME vars, or "Custom" would list in its dropdown and paint nothing.
 */
export function customSchemeVars(s) {
    if ((s.colorScheme ?? 'light') !== 'custom')
        return {};
    const customBg = resolveColor(s.customBackground, '#1E3A54');
    const customText = resolveColor(s.customText, '#ffffff');
    return {
        '--aqx-scheme-bg': customBg,
        '--aqx-scheme-text': customText,
        '--aqx-scheme-accent': customText,
        '--aqx-scheme-surface': `color-mix(in srgb, ${customBg} 88%, ${customText})`,
        '--aqx-scheme-border': `color-mix(in srgb, ${customText} 16%, ${customBg})`,
        // The accent is set to customText (so accent-COLOURED text reads on the band). A solid button fills
        // with the accent and paints its LABEL with `--aqx-theme-accent-contrast` — which otherwise stays the
        // theme default (#fff) → a white button with white text on a custom band (founder bug 2026-07-26).
        // Pin the on-accent label to the band background so the fill/label always contrast.
        '--aqx-theme-accent-contrast': customBg,
    };
}
/**
 * The root `<section>` props every product section shares: `data-scheme` (the only thing that swaps the
 * color palette), scheme-driven bg/text, and the chosen vertical padding. The inner content wrapper caps
 * its width via {@link contentWidthStyle}.
 */
export function universalSectionProps(s) {
    const scheme = s.colorScheme ?? 'light';
    const pt = typeof s.paddingTop === 'number' ? s.paddingTop : 48;
    const pb = typeof s.paddingBottom === 'number' ? s.paddingBottom : 48;
    // Mobile padding falls back to ~half the desktop padding (tighter phones) when not explicitly set.
    const mpt = typeof s.mobilePaddingTop === 'number' ? s.mobilePaddingTop : Math.round(pt / 2);
    const mpb = typeof s.mobilePaddingBottom === 'number' ? s.mobilePaddingBottom : Math.round(pb / 2);
    const isCustom = scheme === 'custom';
    const customVars = customSchemeVars(s);
    return {
        'data-scheme': isCustom ? 'light' : scheme,
        style: {
            // A new block-formatting context so a first/last CHILD's margin can never collapse THROUGH the
            // section: with padding 0, a heading's 15px margin-top used to escape the band and push the whole
            // coloured section down, leaving a strip of bare page (white) under a section divider — the
            // "espace vide entre le divider et le rich text" report (2026-09-11). flow-root is otherwise block.
            display: 'flow-root',
            paddingTop: `${pt}px`,
            paddingBottom: `${pb}px`,
            paddingInline: 'var(--aqx-theme-gutter, 40px)',
            ...customVars,
            ...mediaRadiusVars(s),
            ...animationVars(s),
            background: 'var(--aqx-scheme-bg)',
            // Shrine renders ALL body copy at 0.9 foreground opacity (root `color:rgba(var(--color-foreground),.9)`)
            // — a soft near-black, not full black. Set once here so every section inherits Shrine's exact text feel
            // (headings/elements that need full strength set their own colour).
            color: 'color-mix(in srgb, var(--aqx-scheme-text) 90%, transparent)',
            fontFamily: 'var(--aqx-theme-font)',
            // Mobile padding swap — consumed by the global ≤749px rule in theme.css.
            '--aqx-mpt': `${mpt}px`,
            '--aqx-mpb': `${mpb}px`,
        },
    };
}
/**
 * Opt-in "Section width" SLIDER for FULL-BLEED sections (hero bands, image-with-text, multirow…):
 * the painted BAND — background + media — takes N% of the page width, centred. 100% (default) is the
 * historical edge-to-edge bleed; ~84% reproduces the reference inset-band anatomy at 1440px.
 */
export const sectionWidthField = {
    id: 'sectionWidth',
    type: 'range',
    label: 'Section width',
    group: GROUP,
    min: 30,
    max: 100,
    step: 1,
    unit: '%',
    default: 100,
    info: 'The whole band (background and media) takes this share of the page width, centred. 100% bleeds edge-to-edge.',
};
/**
 * Normalise a stored section-width value to a 30–100 percent number. Tolerates the short-lived
 * select values ('full' → 100, 'page' → 84) and anything malformed (→ 100, the full bleed).
 */
export function bandWidthPercent(value) {
    if (value === 'page')
        return 84;
    if (typeof value !== 'number' || Number.isNaN(value))
        return 100;
    return Math.min(100, Math.max(30, Math.round(value)));
}
/**
 * Split {@link universalSectionProps}' style for the contained-band mode: the SECTION keeps the scheme
 * vars/typography (they must cascade) but goes transparent and un-padded; the BAND wrapper takes the
 * background + paddings (and the `--aqx-mpt/--aqx-mpb` mobile-padding vars — the global ≤749px rule
 * matches any element carrying them inline) plus the page-container width cap.
 */
export function containedBandSplit(style, percent) {
    const src = style;
    const section = { ...src, background: 'transparent' };
    delete section.paddingTop;
    delete section.paddingBottom;
    delete section.paddingInline;
    delete section['--aqx-mpt'];
    delete section['--aqx-mpb'];
    const band = {
        paddingTop: src.paddingTop,
        paddingBottom: src.paddingBottom,
        paddingInline: src.paddingInline,
        background: src.background,
        '--aqx-mpt': src['--aqx-mpt'],
        '--aqx-mpb': src['--aqx-mpb'],
        inlineSize: `${percent}%`,
        marginInline: 'auto',
    };
    return { section: section, band: band };
}
/** The inner content frame: max-width per `width`, centred. `full` lets the section bleed edge-to-edge. */
export function contentWidthStyle(s) {
    const width = s.width ?? 'standard';
    return {
        maxWidth: WIDTH_MAX[width],
        marginInline: 'auto',
        width: '100%',
    };
}
//# sourceMappingURL=universal.js.map